import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import contractService from '../services/contractService';
import './VaultSourceList.css';
import './ContractsList.css';

const parseApplicableTo = (val) => {
  if (!val) return [];
  return val.split(',').filter(Boolean);
};

const ContractsList = () => {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [searchTerm, setSearchTerm]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');

  useEffect(() => { loadContracts(); }, []);

  const loadContracts = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await contractService.getContracts();
      setContracts(res.contracts || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete contract "${name}"?`)) return;
    try {
      await contractService.deleteContract(id);
      setContracts(c => c.filter(x => x.id !== id));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete contract');
    }
  };

  const filtered = contracts.filter(c => {
    if (statusFilter && c.status !== statusFilter) return false;
    if (activeFilter === 'active'   && !c.is_active)  return false;
    if (activeFilter === 'inactive' &&  c.is_active)  return false;
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return c.name?.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q) || c.version?.toLowerCase().includes(q);
  });

  if (loading) return <div className="loading">Loading contracts...</div>;

  return (
    <div className="vaultsource-list-container">
      <div className="header">
        <h1>Contracts</h1>
        <button className="btn btn-primary" onClick={() => navigate('/admin/contracts/new')}>
          + New Contract
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by name, version or description..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="filter-select">
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="archived">Archived</option>
        </select>
        <select value={activeFilter} onChange={e => setActiveFilter(e.target.value)} className="filter-select">
          <option value="">Active &amp; Inactive</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>
        <button className="btn btn-secondary" onClick={() => { setSearchTerm(''); setStatusFilter(''); setActiveFilter(''); }}>
          Clear
        </button>
      </div>

      <div className="table-container">
        <table className="vaultsource-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Version</th>
              <th>Applicable To</th>
              <th>Description</th>
              <th>Status</th>
              <th>Updated</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '24px', color: '#6b7280' }}>
                  No contracts found
                </td>
              </tr>
            ) : (
              filtered.map(c => (
                <tr key={c.id} className={!c.is_active ? 'contracts-row-inactive' : ''}>
                  <td>{c.name}</td>
                  <td>
                    {c.version}
                    {(c.is_default === true || c.is_default === 1) && (
                      <span className="contracts-badge contracts-badge-default" style={{ marginLeft: '6px' }}>Default</span>
                    )}
                  </td>
                  <td>
                    <div className="contracts-applicable-badges">
                      {parseApplicableTo(c.applicable_to).map(t => (
                        <span key={t} className={`contracts-badge contracts-badge-entity contracts-badge-entity-${t.toLowerCase()}`}>{t}</span>
                      ))}
                    </div>
                  </td>
                  <td>{c.description || '—'}</td>
                  <td>
                    <span className={`contracts-status-badge ${c.status}`}>{c.status}</span>
                    {!(c.is_active === true || c.is_active === 1) && (
                      <span className="contracts-badge contracts-badge-inactive" style={{ marginLeft: '6px' }}>Inactive</span>
                    )}
                  </td>
                  <td>{c.updated_at ? new Date(c.updated_at).toLocaleDateString() : '—'}</td>
                  <td className="actions-cell">
                    <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/admin/contracts/${c.id}/edit`)}>
                      Edit
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(c.id, c.name)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ContractsList;
